/**
 * EmailJS contact-form helper.
 * --------------------------------------------------------------------
 * Sends every /demo (contact us) submission to every address listed in
 * `RECIPIENTS`. Hits EmailJS's public REST endpoint via `fetch` — no
 * SDK dependency required.
 *
 * The helper sends these template variables (matching the existing
 * Trialion contact-us template):
 *   {{name}}     — submitter's full name
 *   {{time}}     — formatted server timestamp
 *   {{message}}  — submitter's free-form note + a tidy block listing
 *                  email, company, role, and active-trials count
 *   {{reply_to}} — submitter's work email, so hitting Reply on the
 *                  notification answers the lead directly
 *   {{to_email}} — the recipient for THIS particular send
 *
 * One-time setup (EmailJS dashboard, https://www.emailjs.com):
 *   1. Add an email service (Gmail / Outlook / SES / SMTP).
 *   2. Use your existing "contact us" template body (it already
 *      references {{name}}, {{time}}, {{message}}).
 *   3. IMPORTANT — for multi-recipient fan-out to work, open the
 *      template's Settings tab and set the "To Email" field to
 *      `{{to_email}}` (do this in dashboard settings, not in the
 *      HTML body). This is what lets the same template be reused
 *      per recipient. Also set "Reply To" to `{{reply_to}}`.
 *   4. (Alternative) Skip step 3 and instead set the template's
 *      "CC" field to a fixed comma-separated list of recipients.
 *      In that case set RECIPIENTS below to a single primary
 *      address and the cc list will broadcast automatically.
 *   5. Copy your Service ID, Template ID, and Public Key.
 *
 * Then in `.env.local` at the project root, add:
 *   NEXT_PUBLIC_EMAILJS_SERVICE_ID=service_xxxxxxx
 *   NEXT_PUBLIC_EMAILJS_TEMPLATE_ID=template_xxxxxxx
 *   NEXT_PUBLIC_EMAILJS_PUBLIC_KEY=xxxxxxxxxxxxxxxx
 *
 * The Public Key is intentionally public (safe to ship to the browser);
 * EmailJS gates abuse via domain allow-listing in their dashboard.
 * --------------------------------------------------------------------
 */

/**
 * Edit this list to control who receives every contact-form submission.
 * Each recipient gets a separate "To:" send — no one sees the other
 * addresses, and a bad address in the list can't silently kill the rest.
 *
 * To add a new recipient, just uncomment one of the placeholder lines
 * below (or paste a new line above the closing bracket).
 */
export const RECIPIENTS: readonly string[] = [
  "narendra@aerialborne.com",
  // "founders@aerialborne.com",
  // "sales@trialion.com",
  // "demos@trialion.com",
];

const EMAILJS_ENDPOINT = "https://api.emailjs.com/api/v1.0/email/send";

// Next.js exposes only `NEXT_PUBLIC_*` env vars to the browser. The
// EmailJS Public Key is intentionally public; abuse is gated by the
// domain allow-list in the EmailJS dashboard.
const SERVICE_ID = process.env.NEXT_PUBLIC_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.NEXT_PUBLIC_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.NEXT_PUBLIC_EMAILJS_PUBLIC_KEY;

export type ContactPayload = {
  name: string;
  email: string;
  company: string;
  role?: string;
  trials?: string;
  message?: string;
};

export class EmailJsConfigError extends Error {
  constructor() {
    super(
      "EmailJS environment variables (NEXT_PUBLIC_EMAILJS_*) are not set. " +
        "See the setup block at the top of src/lib/contactEmail.ts.",
    );
    this.name = "EmailJsConfigError";
  }
}

export class EmailJsSendError extends Error {
  constructor(public readonly recipient: string, public readonly status: number, public readonly body: string) {
    super(`EmailJS rejected the send to ${recipient} (HTTP ${status}): ${body}`);
    this.name = "EmailJsSendError";
  }
}

/** Format a readable timestamp for {{time}}. */
function formatTime(now: Date = new Date()): string {
  return now.toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

const dashIfEmpty = (s?: string): string => {
  const v = (s ?? "").trim();
  return v === "" ? "—" : v;
};

/** Build the template variables passed to EmailJS for a single recipient. */
function buildTemplateParams(payload: ContactPayload, recipient: string): Record<string, string> {
  return {
    // Variables referenced by the template HTML body.
    name: payload.name.trim(),
    company: payload.company.trim(),
    email: payload.email.trim(),
    role: dashIfEmpty(payload.role),
    trials: dashIfEmpty(payload.trials),
    message: dashIfEmpty(payload.message) === "—" ? "(No additional notes)" : payload.message!.trim(),
    time: formatTime(),

    // Variables referenced by the template's Settings tab (To / Reply-To
    // fields — these sit outside the HTML body in the EmailJS dashboard).
    to_email: recipient,
    // A patient form may carry a phone number and no email. EmailJS still
    // wants an address here, so a reply then goes back to the inbox it
    // arrived in rather than to nowhere.
    reply_to: payload.email.trim() || recipient,
  };
}

/** Send the form to a single recipient. Throws `EmailJsSendError` on non-2xx. */
async function sendOne(payload: ContactPayload, recipient: string): Promise<void> {
  const res = await fetch(EMAILJS_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      service_id: SERVICE_ID,
      template_id: TEMPLATE_ID,
      user_id: PUBLIC_KEY,
      template_params: buildTemplateParams(payload, recipient),
    }),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new EmailJsSendError(recipient, res.status, body);
  }
}

/**
 * Send a contact-form submission to every address in `RECIPIENTS`.
 *
 * Uses `Promise.allSettled` so a single failed recipient (e.g., a
 * mistyped address) does not silently drop the rest. If every recipient
 * fails, the function throws — callers can show an error toast and let
 * the user retry. If at least one succeeds, the function resolves with
 * the per-recipient outcome so the caller can decide whether to surface
 * a partial-success message.
 */
export async function sendContactEmail(payload: ContactPayload): Promise<{
  ok: string[];
  failed: { recipient: string; error: Error }[];
}> {
  if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
    throw new EmailJsConfigError();
  }

  const results = await Promise.allSettled(RECIPIENTS.map((r) => sendOne(payload, r)));

  const ok: string[] = [];
  const failed: { recipient: string; error: Error }[] = [];
  results.forEach((r, i) => {
    const recipient = RECIPIENTS[i];
    if (r.status === "fulfilled") {
      ok.push(recipient);
    } else {
      failed.push({
        recipient,
        error: r.reason instanceof Error ? r.reason : new Error(String(r.reason)),
      });
    }
  });

  if (ok.length === 0) {
    // Total failure — surface the first error to the caller.
    throw failed[0]?.error ?? new Error("Failed to send contact email");
  }

  return { ok, failed };
}
